// ─── Account taxonomy ────────────────────────────────────────────────────────
//
// The canonical codes identify accounting categories across the whole product —
// the bookkeeping classifier, the cross-check engine, and the AFSTool cascades all
// speak them. They live here rather than in the AFS tool's helpers because a
// service had to reach into a component folder to read them, which is the
// dependency the wrong way round; AFSTool re-exports them so its own callers are
// unchanged.
//
// CANONICAL — internal canonical codes that identify accounting categories
// across the AFSTool cascades, account-mapping proposals, and AJE proposals.
// The codes are dotted paths the server-side cross-check engine also uses.
// Naming convention: short PascalCase-ish, usually dropping the redundant
// final segment of the dotted path (so EQUIPMENT, not EQUIPMENT_EQUIPMENT).

export const CANONICAL = {
	// Current assets
	CASH: "ASSET.CURRENT.CASH.CASH",                                            // server addition
	TRADE_RECEIVABLE: "ASSET.CURRENT.RECEIVABLES.ACCOUNTS_RECEIVABLE_TRADE",    // renamed to match server (RECEIVABLES + _TRADE suffix)
	OTHER_RECEIVABLE: "ASSET.CURRENT.RECEIVABLES.OTHER_RECEIVABLES",            // renamed to match server (pluralised)
	PREPAID_EXPENSES: "ASSET.CURRENT.PREPAIDS.OTHER_PREPAID_EXPENSES",                // renamed to match server (PREPAIDS group; no generic PREPAID_EXPENSES leaf exists)
	TAX_INSTALMENTS: "ASSET.CURRENT.PREPAIDS.TAX_INSTALMENTS",                  // server addition
	INVENTORY: "ASSET.CURRENT.INVENTORY.INVENTORY_FOR_RESALE",

	// Non-current assets
	EQUIPMENT: "ASSET.NONCURRENT.CAPITAL.MACHINERY_AND_EQUIPMENT",                     // renamed to match server (no bare EQUIPMENT leaf exists; same 1740 GIFI line)
	COMPUTER_EQUIPMENT: "ASSET.NONCURRENT.CAPITAL.COMPUTER_EQUIPMENT",
	ACCUMULATED_DEPRECIATION: "ASSET.NONCURRENT.CAPITAL.ACCUMULATED_DEPRECIATION",
	INTANGIBLE_SOFTWARE: "ASSET.NONCURRENT.INTANGIBLE.SOFTWARE",
	ACCUMULATED_AMORTIZATION: "ASSET.NONCURRENT.INTANGIBLE.ACCUMULATED_AMORTIZATION",

	// Liabilities
	ACCOUNTS_PAYABLE: "LIABILITY.CURRENT.PAYABLES.ACCOUNTS_PAYABLE_TRADE",      // renamed to match server (PAYABLES + _TRADE suffix)
	ACCRUED_LIABILITIES: "LIABILITY.CURRENT.PAYABLES.ACCRUED_LIABILITIES",      // renamed to match server (under PAYABLES, not ACCRUED)
	ACCRUED_INTEREST: "LIABILITY.CURRENT.PAYABLES.ACCRUED_INTEREST",            // server addition (slotted under PAYABLES consistently with siblings)
	INCOME_TAX_PAYABLE: "LIABILITY.CURRENT.TAXES.INCOME_TAX_PAYABLE",
	BANK_OVERDRAFT: "LIABILITY.CURRENT.OTHER.BANK_OVERDRAFT",
	DEFERRED_REVENUE: "LIABILITY.CURRENT.DEFERRED.DEFERRED_REVENUE",            // renamed to match server (DEFERRED, not DEFERRED_REVENUE)
	CUSTOMER_DEPOSITS: "LIABILITY.CURRENT.DEFERRED.CUSTOMER_DEPOSITS",          // server addition (under DEFERRED consistently with DEFERRED_REVENUE)
	BANK_LOAN: "LIABILITY.NONCURRENT.DEBT.BANK_LOAN",

	// Equity
	COMMON_SHARES: "EQUITY.CONTRIBUTED.SHARE_CAPITAL",                          // renamed to match server (CONTRIBUTED.SHARE_CAPITAL)
	OWNER_CAPITAL: "EQUITY.OWNER_CAPITAL.OWNER_CAPITAL",
	DIVIDENDS_DECLARED: "EQUITY.RETAINED.DIVIDENDS_DECLARED",                   // renamed to match server (under RETAINED, not DIVIDENDS)
	RETAINED_EARNINGS: "EQUITY.RETAINED.RETAINED_EARNINGS_OPENING",             // aliased to RETAINED_EARNINGS_OPENING (sole consumer: CA_04 prior-period write-off)
	RETAINED_EARNINGS_OPENING: "EQUITY.RETAINED.RETAINED_EARNINGS_OPENING",

	// Revenue
	TRADE_REVENUE: "REVENUE.OPERATING.SALES.TRADE_REVENUE",
	SALES_RETURNS: "REVENUE.OPERATING.SALES.SALES_RETURNS",

	// Expenses
	COGS: "EXPENSE.COGS.COGS.COST_OF_GOODS_SOLD",
	DEPRECIATION_EXPENSE: "EXPENSE.OPERATING.DEPRECIATION.DEPRECIATION_EXPENSE",
	AMORTIZATION_EXPENSE: "EXPENSE.OPERATING.AMORTIZATION.AMORTIZATION_EXPENSE",
	INTEREST_EXPENSE: "EXPENSE.NONOPERATING.FINANCE.INTEREST_EXPENSE",          // renamed to match server (NONOPERATING.FINANCE)
	INCOME_TAX_EXPENSE: "EXPENSE.NONOPERATING.OTHER.INCOME_TAX_EXPENSE",        // renamed to match server (NONOPERATING.OTHER)
	BANK_CHARGES: "EXPENSE.NONOPERATING.FINANCE.BANK_CHARGES",                  // server addition (alongside INTEREST_EXPENSE in same group)
	PAYROLL_EXPENSE: "EXPENSE.OPERATING.ADMIN.WAGES",                           // renamed to match server (WAGES)
	OPERATING_EXPENSE: "EXPENSE.OPERATING.OTHER.GENERAL",
	ACCRUED_EXPENSES: "EXPENSE.OPERATING.OTHER.GENERAL",                        // aliased to OPERATING_EXPENSE — the canonical is the DEBIT (expense) side of an accrual AJE; the liability side uses ACCRUED_LIABILITIES
	LOSS_ON_DISPOSAL: "EXPENSE.OPERATING.OTHER.LOSS_ON_DISPOSAL",

	// Suspense
	BS_DIFFERENCE: "SUSPENSE.UNRESOLVED.UNRESOLVED.BS_DIFFERENCE",              // server addition
	CLEARING_ACCOUNT: "SUSPENSE.UNRESOLVED.UNRESOLVED.CLEARING_ACCOUNT",

	// Payroll-side liability sub-line that BookkeepingService uses when a
	// government payment matches a payroll-remittance pattern (separate from
	// INCOME_TAX_PAYABLE, which is for corporate income tax).
	PAYROLL_SOURCE_DEDUCTIONS: "LIABILITY.CURRENT.TAXES.PAYROLL_SOURCE_DEDUCTIONS",
};

// GIFI — General Index of Financial Information codes (CRA tax-form line
// numbers). Numeric IDs the AJE proposals carry alongside canonical codes for
// downstream tax-form mapping.
//
// All values verified against CRA RC4088 (the official GIFI publication)
// using column-aware extraction of the listing PDF. Every value is a real
// CRA line that AJEs can be posted to — no subtotals or invented codes.

export const GIFI = {
	// Assets
	CASH: 1001,                                    // Cash
	TRADE_RECEIVABLE: 1062,                        // Trade accounts receivable (specific)
	OTHER_RECEIVABLE: 1060,                        // Accounts receivable (parent — for "other receivables")
	ALLOWANCE_FOR_DOUBTFUL: 1063,                  // Allowance for doubtful trade accounts receivable
	PREPAID_EXPENSES: 1484,                        // Prepaid expenses
	INVENTORY: 1121,                               // Inventory of goods for sale
	// Tangible capital assets (gross, paired with their accumulated lines).
	MACHINERY_AND_EQUIPMENT: 1740,                 // Machinery, equipment, furniture, and fixtures (combined)
	COMPUTER_EQUIPMENT: 1774,                      // Computer equipment/software

	// Accumulated depreciation (tangible) — CRA splits this per asset class.
	// Default uses 1741 (Machinery/equipment/furniture/fixtures combined —
	// the most generic real CRA line) so cascades without class context still
	// emit a postable code. 2009 is the cross-class subtotal — exposed as
	// _TANGIBLE_TOTAL for explicit subtotal reporting but NOT a default
	// (subtotals are computed lines, not posting targets per CRA convention).
	ACCUMULATED_DEPRECIATION: 1741,                                // Accumulated amortization of machinery, equipment, furniture, and fixtures (catchall default)
	ACCUMULATED_DEPRECIATION_TANGIBLE_TOTAL: 2009,                 // Subtotal — sum of all per-class accumulated amortization (do NOT post to)
	ACCUMULATED_DEPRECIATION_BUILDINGS: 1681,                      // Accumulated amortization of buildings
	ACCUMULATED_DEPRECIATION_MANUFACTURING_PLANT: 1683,            // Accumulated amortization of manufacturing and processing plant
	ACCUMULATED_DEPRECIATION_MACHINERY: 1741,                      // Accumulated amortization of machinery, equipment, furniture, and fixtures (combined — same value as default)
	ACCUMULATED_DEPRECIATION_VEHICLES: 1743,                       // Accumulated amortization of motor vehicles
	ACCUMULATED_DEPRECIATION_COMPUTER_EQUIPMENT: 1775,             // Accumulated amortization of computer equipment/software
	ACCUMULATED_DEPRECIATION_FURNITURE: 1788,                      // Accumulated amortization of furniture and fixtures (specific)

	// Accumulated amortization (intangible) — 2011 IS a real CRA line
	// ("Accumulated amortization of intangible assets" — generic intangible),
	// so it works as the default. 2179 is the subtotal.
	ACCUMULATED_AMORTIZATION: 2011,                                // Accumulated amortization of intangible assets (generic — real CRA line, OK as default)
	ACCUMULATED_AMORTIZATION_INTANGIBLE_TOTAL: 2179,               // Subtotal — sum across classes (do NOT post to)
	ACCUMULATED_AMORTIZATION_GOODWILL: 2013,                       // Accumulated amortization of goodwill
	ACCUMULATED_AMORTIZATION_QUOTA: 2015,                          // Accumulated amortization of quota
	ACCUMULATED_AMORTIZATION_LICENCES: 2017,                       // Accumulated amortization of licences
	ACCUMULATED_AMORTIZATION_INCORPORATION_COSTS: 2019,            // Accumulated amortization of incorporation costs
	ACCUMULATED_AMORTIZATION_TRADEMARKS_PATENTS: 2021,             // Accumulated amortization of trademarks/patents
	ACCUMULATED_AMORTIZATION_CUSTOMER_LISTS: 2023,                 // Accumulated amortization of customer lists

	// Liabilities
	DEFERRED_REVENUE: 2770,                        // Deferred income
	INCOME_TAX_PAYABLE: 2680,                      // Taxes payable
	PAYROLL_SOURCE_DEDUCTIONS: 2627,               // Employee deductions payable (CRA-specific, distinct from corp income tax)
	BANK_OVERDRAFT: 2600,                          // Bank overdraft (bank indebtedness)

	// AP / accruals sub-precision (CRA 2620–2630 range)
	// 2620 is the parent ("Amounts payable and accrued liabilities" — payables
	// and accruals lumped together). The sub-codes are more precise for T2.
	// Pick the most specific code the cascade has enough context to know.
	ACCRUED_LIABILITIES: 2620,                     // parent: Amounts payable and accrued liabilities — generic accruals
	TRADE_PAYABLE: 2621,                           // Trade payables
	TRADE_PAYABLE_RELATED_PARTIES: 2622,           // Trade payables to related parties
	HOLDBACKS_PAYABLE: 2623,                       // Holdbacks payable
	WAGES_PAYABLE: 2624,                           // Wages payable
	MANAGEMENT_FEES_PAYABLE: 2625,                 // Management fees payable
	BONUSES_PAYABLE: 2626,                         // Bonuses payable
	EMPLOYEE_DEDUCTIONS_PAYABLE: 2627,             // Employee deductions payable
	WITHHOLDING_TAXES_PAYABLE: 2628,               // Withholding taxes payable
	ACCRUED_INTEREST: 2629,                        // Interest payable / accrued interest
	NPO_PAYABLE: 2630,                             // Amounts payable to members of NPOs

	// "Other current X" parent lines (2960 Other current liabilities, 2961
	// Deposits received) and the related categories
	CUSTOMER_DEPOSITS: 2961,                       // Deposits received — used for customer overpayments/deposits

	// Equity
	COMMON_SHARES: 3500,                           // Common shares
	RETAINED_EARNINGS_OPENING: 3660,               // Retained earnings/deficit – Start
	DIVIDENDS_DECLARED: 3700,                      // Dividends declared
	PRIOR_PERIOD_ADJUSTMENTS: 3720,                // Prior period adjustments — distinct from opening RE

	// Long-term debt
	BANK_LOAN: 3143,                               // Chartered bank loan
	INTANGIBLE_ASSETS: 2010,                       // Intangible assets (asset side, paired with 2011 accumulated)

	// Revenue
	SALES: 8000,                                   // Trade sales of goods and services
	SALES_RETURNS: 8000,                           // CRA nets returns against sales — no dedicated code

	// Expenses
	COST_OF_GOODS_SOLD: 8518,                      // Cost of sales
	LOSS_ON_DISPOSAL: 8210,                        // Realized gains/losses on disposal of assets
	BAD_DEBT: 8590,                                // Bad debt expense
	SALARIES_AND_WAGES: 9060,                      // Salaries and wages
	INTEREST_EXPENSE: 8710,                        // Interest and bank charges (parent — interest portion)
	BANK_CHARGES: 8715,                            // Bank charges (specific sub-line under 8710)
	AMORTIZATION_EXPENSE: 8570,                    // Amortization of intangible assets
	DEPRECIATION_EXPENSE: 8670,                    // Amortization of tangible assets (= book depreciation)
	INCOME_TAX_EXPENSE: 9990,                      // Current income taxes
	INCOME_TAX_PROVISION: 9990,                    // Current income taxes — alias (same line item)

	// Aliases — distinct names that resolve to a specific CRA line item
	OPERATING_EXPENSE: 9270,                       // Other expenses — used as the catchall for general operating-expense AJEs
	ACCOUNTS_PAYABLE: 2621,                        // == TRADE_PAYABLE (the AP canonical is trade-specific)
	ACCRUED_EXPENSES: 2620,                        // == ACCRUED_LIABILITIES — generic accrual; used by the AJE template for the liability side

	// BS_01 suspense — when the cascade can't reconcile the balance sheet
	// it parks the difference in a "suspense" account for later
	// investigation. Suspense is asset-side or liability-side depending on
	// which side is short; pick the right CRA "Other current ..." parent.
	// Both are real CRA lines, so the resulting AJEs file cleanly.
	SUSPENSE_BS_DIFFERENCE_ASSET: 1480,            // Other current assets — when assets are short and suspense is a debit
	SUSPENSE_BS_DIFFERENCE_LIABILITY: 2960,        // Other current liabilities — when liabilities are short and suspense is a credit
};

// ─── CANONICAL ↔ GIFI pairing ────────────────────────────────────────────────
//
// Single source of truth for "which GIFI code goes with which canonical
// account category." Cascades should NEVER specify debit/creditGifi directly
// — they should only specify the canonical code, and the dispatcher derives
// the GIFI here. This makes (canonical, gifi) mismatches structurally
// impossible.
//
// Adding a new CANONICAL entry without a matching row in this map will throw
// at module load (see the validation at the bottom of this file). That's
// intentional — it forces the pairing to stay complete.

export const CANONICAL_TO_GIFI = {
	// Current assets
	[CANONICAL.CASH]: GIFI.CASH,
	[CANONICAL.TRADE_RECEIVABLE]: GIFI.TRADE_RECEIVABLE,           // 1062 Trade accounts receivable
	[CANONICAL.OTHER_RECEIVABLE]: GIFI.OTHER_RECEIVABLE,           // 1060 Accounts receivable (parent)
	[CANONICAL.PREPAID_EXPENSES]: GIFI.PREPAID_EXPENSES,
	[CANONICAL.TAX_INSTALMENTS]: GIFI.PREPAID_EXPENSES,            // taxes recoverable also rolls up to prepaid range
	[CANONICAL.INVENTORY]: GIFI.INVENTORY,

	// Non-current assets — gross asset canonicals → gross asset GIFIs
	[CANONICAL.EQUIPMENT]: GIFI.MACHINERY_AND_EQUIPMENT,            // 1740 Machinery, equipment, furniture, and fixtures
	[CANONICAL.COMPUTER_EQUIPMENT]: GIFI.COMPUTER_EQUIPMENT,        // 1774 Computer equipment/software
	[CANONICAL.ACCUMULATED_DEPRECIATION]: GIFI.ACCUMULATED_DEPRECIATION,
	[CANONICAL.INTANGIBLE_SOFTWARE]: GIFI.INTANGIBLE_ASSETS,        // 2010 Intangible assets
	[CANONICAL.ACCUMULATED_AMORTIZATION]: GIFI.ACCUMULATED_AMORTIZATION,

	// Liabilities
	[CANONICAL.ACCOUNTS_PAYABLE]: GIFI.ACCOUNTS_PAYABLE,
	[CANONICAL.ACCRUED_LIABILITIES]: GIFI.ACCRUED_LIABILITIES,
	[CANONICAL.ACCRUED_INTEREST]: GIFI.ACCRUED_INTEREST,
	[CANONICAL.INCOME_TAX_PAYABLE]: GIFI.INCOME_TAX_PAYABLE,
	[CANONICAL.BANK_OVERDRAFT]: GIFI.BANK_OVERDRAFT,
	[CANONICAL.DEFERRED_REVENUE]: GIFI.DEFERRED_REVENUE,
	[CANONICAL.CUSTOMER_DEPOSITS]: GIFI.CUSTOMER_DEPOSITS,
	[CANONICAL.BANK_LOAN]: GIFI.BANK_LOAN,                         // 3143 Chartered bank loan
	[CANONICAL.PAYROLL_SOURCE_DEDUCTIONS]: GIFI.PAYROLL_SOURCE_DEDUCTIONS,  // 2627 Employee deductions payable

	// Equity
	[CANONICAL.COMMON_SHARES]: GIFI.COMMON_SHARES,
	[CANONICAL.OWNER_CAPITAL]: GIFI.COMMON_SHARES,                 // partnership equity rolls to share capital line for T2
	[CANONICAL.DIVIDENDS_DECLARED]: GIFI.DIVIDENDS_DECLARED,       // 3700 Dividends declared
	[CANONICAL.RETAINED_EARNINGS]: GIFI.RETAINED_EARNINGS_OPENING,
	[CANONICAL.RETAINED_EARNINGS_OPENING]: GIFI.RETAINED_EARNINGS_OPENING,

	// Revenue
	[CANONICAL.TRADE_REVENUE]: GIFI.SALES,
	[CANONICAL.SALES_RETURNS]: GIFI.SALES_RETURNS,

	// Expenses
	[CANONICAL.COGS]: GIFI.COST_OF_GOODS_SOLD,
	[CANONICAL.DEPRECIATION_EXPENSE]: GIFI.DEPRECIATION_EXPENSE,
	[CANONICAL.AMORTIZATION_EXPENSE]: GIFI.AMORTIZATION_EXPENSE,
	[CANONICAL.INTEREST_EXPENSE]: GIFI.INTEREST_EXPENSE,
	[CANONICAL.INCOME_TAX_EXPENSE]: GIFI.INCOME_TAX_EXPENSE,
	[CANONICAL.BANK_CHARGES]: GIFI.BANK_CHARGES,                   // 8715 Bank charges (specific sub-line)
	[CANONICAL.PAYROLL_EXPENSE]: GIFI.SALARIES_AND_WAGES,           // 9060 Salaries and wages
	[CANONICAL.OPERATING_EXPENSE]: GIFI.OPERATING_EXPENSE,
	[CANONICAL.ACCRUED_EXPENSES]: GIFI.OPERATING_EXPENSE,           // when used as expense side; for the liability side use ACCRUED_LIABILITIES
	[CANONICAL.LOSS_ON_DISPOSAL]: GIFI.LOSS_ON_DISPOSAL,

	// Suspense
	[CANONICAL.BS_DIFFERENCE]: GIFI.SUSPENSE_BS_DIFFERENCE_ASSET,   // default to asset-side; the BS_01 cascade picks the right side explicitly
	[CANONICAL.CLEARING_ACCOUNT]: GIFI.SUSPENSE_BS_DIFFERENCE_ASSET,
};

// gifiFor — the single way callers should derive a GIFI code from a canonical.
// Hardcoding a GIFI in a cascade entry is a bug; always go through this helper
// (the ESLint rule no-restricted-syntax enforces this for cascade files).
export function gifiFor(canonicalCode) {
	let gifi = CANONICAL_TO_GIFI[canonicalCode];
	if (gifi == null) {
		throw new Error("No GIFI mapping for canonical code '" + canonicalCode + "'. Add an entry to CANONICAL_TO_GIFI in constants/CanonicalCodes.js.");
	}
	return gifi;
}

// Load-time validation: every entry in CANONICAL must have a paired entry in
// CANONICAL_TO_GIFI. If a developer adds a new CANONICAL constant but forgets
// the GIFI pairing, the app crashes immediately on import — making the error
// impossible to ship.
(function validateCanonicalGifiPairing() {
	let missing = [];
	for (let key of Object.keys(CANONICAL)) {
		let code = CANONICAL[key];
		if (!(code in CANONICAL_TO_GIFI)) missing.push(key + " (" + code + ")");
	}
	if (missing.length > 0) {
		throw new Error("CANONICAL_TO_GIFI is missing pairings for: " + missing.join(", ") + ". Every CANONICAL entry must have a GIFI mapping. Add the missing pairings in constants/CanonicalCodes.js.");
	}
})();
